import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useAdminStore } from "@/hooks/useAdminStore";
import AdminLogin from "./components/AdminLogin";
import ProductsPanel from "./components/ProductsPanel";
import EditorialPanel from "./components/EditorialPanel";
import OrdersPanel from "./components/OrdersPanel";

type Tab = "products" | "editorial" | "orders";

export default function AdminPage() {
  const { isAdmin, isLoggedIn, login, logout, loading: authLoading } = useAuth();
  const {
    products: adminProducts,
    posts: adminPosts,
    loading,
    loadProducts,
    loadPosts,
    toggleProduct,
    updateProduct,
    addProduct,
    deleteProduct,
    togglePost,
  } = useAdminStore();

  const [activeTab, setActiveTab] = useState<Tab>("products");

  // 加载数据
  useEffect(() => {
    if (isAdmin) {
      loadProducts();
      loadPosts();
    }
  }, [isAdmin, loadProducts, loadPosts]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#f7f6f4] flex items-center justify-center">
        <div className="text-sm text-[#aaa]">Loading...</div>
      </div>
    );
  }

  if (!isLoggedIn || !isAdmin) {
    return <AdminLogin onLogin={login} />;
  }

  const tabs: { key: Tab; label: string; icon: string; count?: number }[] = [
    {
      key: "products",
      label: "Products",
      icon: "ri-shopping-bag-line",
      count: adminProducts.length,
    },
    {
      key: "editorial",
      label: "Glow Edit",
      icon: "ri-article-line",
      count: adminPosts.length,
    },
    {
      key: "orders",
      label: "Orders",
      icon: "ri-file-list-3-line",
    },
  ];

  return (
    <div className="min-h-screen bg-[#f7f6f4] flex flex-col">
      {/* Top Bar */}
      <header className="bg-white border-b border-[#f0f0f0] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="font-['Playfair_Display'] text-xl tracking-widest text-[#1a1a1a] uppercase">
            i-if
          </h1>
          <span className="text-xs tracking-[0.15em] text-[#bbb] uppercase">Admin Console</span>
        </div>

        <div className="flex items-center gap-4">
          <a
            href="/"
            className="flex items-center gap-1.5 text-xs text-[#888] hover:text-[#1a1a1a] transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-store-2-line text-sm"></i>
            </div>
            View Store
          </a>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-xs text-[#888] hover:text-[#1a1a1a] transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-logout-box-line text-sm"></i>
            </div>
            Log Out
          </button>
        </div>
      </header>

      <div className="flex flex-1 max-w-6xl mx-auto w-full px-6 py-8 gap-6">
        {/* Sidebar */}
        <aside className="w-52 flex-shrink-0">
          <nav className="space-y-1">
            <div className="text-xs tracking-[0.15em] text-[#bbb] uppercase px-3 mb-3">
              Manage
            </div>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${
                  activeTab === tab.key
                    ? "bg-[#1a1a1a] text-white"
                    : "text-[#555] hover:bg-[#f0f0ee]"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${tab.icon} text-sm`}></i>
                  </div>
                  <span>{tab.label}</span>
                </div>
                {tab.count !== undefined && (
                  <span
                    className={`text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center ${
                      activeTab === tab.key
                        ? "bg-white/20 text-white"
                        : "bg-[#eee] text-[#888]"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Quick Stats */}
          <div className="mt-8 bg-white rounded-xl p-4 border border-[#f0f0f0]">
            <div className="text-xs tracking-[0.12em] text-[#bbb] uppercase mb-3">Quick Stats</div>
            <div className="space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-[#888]">Active Products</span>
                <span className="text-[#1a1a1a] font-medium">
                  {adminProducts.filter((p) => p.isActive).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#888]">Published Posts</span>
                <span className="text-[#1a1a1a] font-medium">
                  {adminPosts.filter((p) => p.isActive).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#888]">New Arrivals</span>
                <span className="text-[#1a1a1a] font-medium">
                  {adminProducts.filter((p) => p.isNew && p.isActive).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#888]">Featured</span>
                <span className="text-[#1a1a1a] font-medium">
                  {adminProducts.filter((p) => p.isFeatured && p.isActive).length}
                </span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="mb-6">
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1a1a1a] mb-1">
              {activeTab === "products" ? "Product Management" :
               activeTab === "editorial" ? "Glow Edit Content" :
               "Order Management"}
            </h2>
            <p className="text-sm text-[#aaa]">
              {activeTab === "products"
                ? "Add, edit, and manage your store products. Toggle to show or hide from customers."
                : activeTab === "editorial"
                ? "Manage your editorial articles. Toggle visibility to publish or unpublish."
                : "View and manage customer orders. Update order status and track deliveries."}
            </p>
          </div>

          {loading && activeTab !== "orders" ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-xl p-6">
                  <div className="bg-[#f0f0f0] h-4 w-1/3 mb-3 rounded" />
                  <div className="bg-[#f0f0f0] h-3 w-1/2 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {activeTab === "products" && (
                <ProductsPanel
                  products={adminProducts}
                  onToggle={toggleProduct}
                  onUpdate={(id, fields) => updateProduct(id, fields)}
                  onAdd={addProduct}
                  onDelete={deleteProduct}
                />
              )}

              {activeTab === "editorial" && (
                <EditorialPanel
                  posts={adminPosts}
                  onToggle={togglePost}
                />
              )}

              {activeTab === "orders" && (
                <OrdersPanel />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
